import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Form } from "react-bootstrap";
import * as actions from "Store/Course/Instructor/OtherLabs/Actions";
// import Options from "../Options";
import parse from 'html-react-parser';
import '../../css/fillInBlanks.scss';
import AnswerExplanation from "../../GradeSubmission/AnswerExplaination";
import { removeRemarks } from "../../GradeSubmission/GradeCheck";
// import log from "loglevel";

const FillBlanks = ({ courseId, question, index, previousResponse, updateResponse, disable, Eval, showMarksToStudent, revealAnswers }) => {
  const [questionResponseId, setQuestionResponseId] = useState(null);
  const [saveResponseTimer, setSaveResponseTimer] = useState(null);
  const [userAnswerText, setUserAnswerText] = useState("");
  const mode = localStorage.getItem("mode");

  const formatAnswerArray = (answerString) =>
    answerString.replace(/'/g, '').split(',').map(answer => answer.trim().toLowerCase());

  const expectedAnswersList = question.questionanswer_set.map(ans => formatAnswerArray(ans?.answer||""));
  const primaryAnswerList = question.questionanswer_set.length > 0
    ? formatAnswerArray(question.questionanswer_set[0]?.answer)
    : [];

  // Smart input parsing: supports both comma-separated and newline-separated
  const parseUserInput = (input) => {
    if (!input) return [];
    const trimmedInput = input.trim();

    // Detect format and parse accordingly
    if (trimmedInput.includes(',')) {
      // Comma-separated format: "answer1, answer2, answer3"
      return trimmedInput.split(',').map(answer => answer.trim().toLowerCase());
    } else {
      // Newline-separated format (existing): "answer1\nanswer2\nanswer3"
      return trimmedInput.toLowerCase().split('\n').map(answer => answer.trim());
    }
  };

  // Check if answers use encoded format (BLANK1:answer, BLANK2:answer)
  const hasEncodedAnswers = (answersList) => {
    return answersList.some(answers =>
      answers.some(answer => answer.includes('blank') && answer.includes(':'))
    );
  };

  // Parse encoded answers into blank-specific groups
  const parseEncodedAnswers = (answersList) => {
    const blankAnswers = {};
    answersList.forEach(answers => {
      answers.forEach(answer => {
        if (answer.includes('blank') && answer.includes(':')) {
          const [blankPart, answerText] = answer.split(':');
          const blankMatch = blankPart.match(/blank(\d+)/i);
          if (blankMatch) {
            const blankNum = parseInt(blankMatch[1]);
            if (!blankAnswers[blankNum]) blankAnswers[blankNum] = [];
            blankAnswers[blankNum].push(answerText.trim());
          }
        }
      });
    });
    return blankAnswers;
  };

  // Validate encoded multi-blank answers
  const validateEncodedAnswers = (userAnswers, answersList) => {
    const blankAnswers = parseEncodedAnswers(answersList);
    return userAnswers.every((userAnswer, idx) => {
      const blankNum = idx + 1;
      const validAnswers = blankAnswers[blankNum] || [];
      return validAnswers.includes(userAnswer.trim());
    });
  };

  // Validate traditional multi-blank answers
  const validateTraditionalAnswers = (userAnswers, answersList) => {
    return userAnswers.every((answer, idx) =>
      answersList[idx]?.includes(answer.trim())
    );
  };

  const validateUserAnswer = useCallback((responseText) => {
    if (!responseText) return { isSingleAnswerCorrect: false, isMultiAnswerCorrect: false };

    const userAnswers = parseUserInput(responseText);
    const isSingleAnswerCorrect = primaryAnswerList.includes(responseText.trim().toLowerCase());

    let isMultiAnswerCorrect = false;
    if (hasEncodedAnswers(expectedAnswersList)) {
      // New encoded format validation
      isMultiAnswerCorrect = validateEncodedAnswers(userAnswers, expectedAnswersList);
    } else {
      // Traditional format validation (backward compatibility)
      isMultiAnswerCorrect = validateTraditionalAnswers(userAnswers, expectedAnswersList);
    }

    return { isSingleAnswerCorrect, isMultiAnswerCorrect };
  }, [primaryAnswerList, expectedAnswersList]);

  useEffect(() => {
    if (previousResponse) {
      if (!questionResponseId) setQuestionResponseId(previousResponse.id || null);
      setUserAnswerText(previousResponse.response_text || "");
    }
  }, [previousResponse]);

  const saveUserResponse = (text) => {
    // Per-question API call removed — local state only. Autosave runs every
    // 30s in student/index.jsx and on Submit Quiz, batching all answers into
    // one POST .../student_submission/?autosave=true.
    if (!disable) {
      updateResponse({
        ...(previousResponse || {}),
        id: question.id,
        response_text: text,
      });
    }
  };

  const handleUserTyping = (value) => {
    clearTimeout(saveResponseTimer);
    setUserAnswerText(value);
    setSaveResponseTimer(setTimeout(() => saveUserResponse(value), 1000));
  };

  const saveScoredMarks = async (marks) => {
    await actions.addScoredMarksRequest(question, questionResponseId, marks);
    updateResponse();
  };

  const saveRemark = async (remark) => {
    await actions.addRemarkRequest(question, questionResponseId, remark);
    updateResponse();
  };

  const getBlankConfiguration = () => {
    try {
      // Debug logging
      // console.log('[FILLBLANKS_DEBUG] Question structure:', {
      //   id: question.id,
      //   questionanswer_set: question.questionanswer_set,
      //   questionanswer_set_length: question.questionanswer_set?.length,
      //   marks: question.marks,
      //   negative_marks: question.negative_marks,
      //   previousResponse: previousResponse
      // });

      // Check if this is a JSON-encoded per-blank configuration
      const answerData = question.questionanswer_set?.[0]?.answer;
      if (answerData?.startsWith('{"blanks":')) {
        // console.log('[FILLBLANKS_DEBUG] Found JSON-encoded blanks configuration');
        return JSON.parse(answerData);
      }

      // UPDATED APPROACH: Use dedicated questionanswer_marks field for per-blank data
      // This field provides marks info without exposing answers to students
      if (question.questionanswer_marks && question.questionanswer_marks.length > 1) {
        // console.log('[FILLBLANKS_DEBUG] Detected multi-blank question from questionanswer_marks with', question.questionanswer_marks.length, 'blanks');

        // Read actual per-blank data from the marks-only field
        const blanks = question.questionanswer_marks.map((marksRecord, index) => {
          const marks = marksRecord.marks || (question.marks / question.questionanswer_marks.length); // fallback to equal distribution
          const negative_marks = marksRecord.negative_marks || 0; // fallback to 0

          // console.log(`[FILLBLANKS_DEBUG] Blank ${index + 1}: marks=${marks}, negative_marks=${negative_marks}`);

          return {
            type: 'text', // default to text, could be enhanced later
            marks: marks,
            neg: negative_marks,
            blank_index: marksRecord.blank_index
          };
        });

        return {
          blanks: blanks
        };
      }

      // Fallback: Check if we have access to full questionanswer_set (for instructors/evaluated quizzes)
      if (question.questionanswer_set && question.questionanswer_set.length > 1) {
        // console.log('[FILLBLANKS_DEBUG] Detected multi-blank question from questionanswer_set with', question.questionanswer_set.length, 'blanks');

        // Read actual per-blank data from the database
        const blanks = question.questionanswer_set.map((answerRecord, index) => {
          const marks = answerRecord.marks || (question.marks / question.questionanswer_set.length); // fallback to equal distribution
          const negative_marks = answerRecord.negative_marks || 0; // fallback to 0

          // console.log(`[FILLBLANKS_DEBUG] Blank ${index + 1}: marks=${marks}, negative_marks=${negative_marks}, answer=${answerRecord.answer}`);

          return {
            type: 'text', // default to text, could be enhanced later
            marks: marks,
            neg: negative_marks,
            answer: answerRecord.answer
          };
        });

        return {
          blanks: blanks
        };
      }

      // Fallback: Check if this is a multi-blank question by looking at the response data
      if (previousResponse?.response_text) {
        const userAnswers = previousResponse.response_text.includes(',')
          ? previousResponse.response_text.split(',').map(a => a.trim())
          : previousResponse.response_text.split('\n').map(a => a.trim()).filter(a => a);

        // If user provided multiple answers, this might be a multi-blank question
        if (userAnswers.length > 1) {
          // console.log('[FILLBLANKS_DEBUG] Detected potential multi-blank question from user response with', userAnswers.length, 'answers');

          // Create configuration for multi-blank question with equal distribution fallback
          const totalMarks = question.marks;
          const marksPerBlank = totalMarks / userAnswers.length;
          const negativeMarksPerBlank = parseFloat(question.negative_marks) || 0;

          const config = {
            blanks: userAnswers.map((answer, index) => ({
              type: 'text',
              marks: marksPerBlank,
              neg: negativeMarksPerBlank,
              answer: answer
            }))
          };

          // console.log('[FILLBLANKS_DEBUG] Created multi-blank configuration from response:', config);
          return config;
        }
      }

      // Fallback: Check if question text contains multiple blanks (count underscores)
      const questionText = question.question_text || '';
      const blankCount = (questionText.match(/____+/g) || []).length;
      if (blankCount > 1) {
        // console.log('[FILLBLANKS_DEBUG] Detected', blankCount, 'blanks from question text');

        const totalMarks = question.marks;
        const marksPerBlank = totalMarks / blankCount;
        const negativeMarksPerBlank = parseFloat(question.negative_marks) || 0;

        const config = {
          blanks: Array(blankCount).fill(null).map((_, index) => ({
            type: 'text',
            marks: marksPerBlank,
            neg: negativeMarksPerBlank,
            answer: `blank${index + 1}`
          }))
        };

        // console.log('[FILLBLANKS_DEBUG] Created multi-blank configuration from question text:', config);
        return config;
      }

      // Check if this is a multi-blank question (multiple answer entries) - legacy approach
      if (question.questionanswer_set && question.questionanswer_set.length > 1) {
        // console.log('[FILLBLANKS_DEBUG] Detected multi-blank question with', question.questionanswer_set.length, 'blanks');

        const totalMarks = question.marks;
        const marksPerBlank = totalMarks / question.questionanswer_set.length;
        const negativeMarksPerBlank = parseFloat(question.negative_marks) || 0;

        const config = {
          blanks: question.questionanswer_set.map((answer, index) => ({
            type: 'text',
            marks: marksPerBlank,
            neg: negativeMarksPerBlank,
            answer: answer.answer
          }))
        };

        // console.log('[FILLBLANKS_DEBUG] Created blank configuration:', config);
        return config;
      }

      // console.log('[FILLBLANKS_DEBUG] Single blank format detected');
      return null; // Single blank format
    } catch (error) {
      // console.error('[FILLBLANKS_DEBUG] Error in getBlankConfiguration:', error);
      return null;
    }
  };

  const renderQuestionText = () => {
    const sanitizedQuestionText = removeRemarks(question?.question_text);
    return (
      <Col xs={12} className="question-text">
        <b>{parse(sanitizedQuestionText)}</b>
      </Col>
    );
  };

  const renderAnswerInput = () => {
    const showValidationBorder = Eval && !revealAnswers;
    const answerValidationStyle = showValidationBorder
      ? (question?.questionresponse_set?.scored_marks === question?.marks)
        ? '2px solid green'
        : question?.questionresponse_set?.scored_marks > 0
          ? '2px solid orange'
          : '2px solid red'
      : {};

    // Determine if this is a multi-blank question and get placeholder text
    const blankConfig = getBlankConfiguration();
    let placeholderText;

    if (blankConfig?.blanks?.length > 1) {
      // Per-blank configuration with simplified answer placeholders
      const answerPlaceholders = blankConfig.blanks.map((blank, i) => {
        return `answer${i + 1}`;
      }).join(', ');
      placeholderText = `Enter answers separated by commas (${answerPlaceholders})`;
    } else {
      // Legacy format
      const isMultiBlank = hasEncodedAnswers(expectedAnswersList) || expectedAnswersList.length > 1;
      placeholderText = isMultiBlank
        ? "Enter answers separated by commas (answer1, answer2, answer3)"
        : "Enter your answer";
    }

    return (
      <Col xs={12}>
        <div style={{ border: answerValidationStyle }}>
          <Form.Control
            type="text"
            value={userAnswerText}
            onChange={({ target: { value } }) => handleUserTyping(value)}
            disabled={disable}
            placeholder={placeholderText}
          />
        </div>
      </Col>
    );
  };

  const cleanAnswerText = (text) => {
    if (!text) return '';
    let cleaned = text.trim();
    if (cleaned.startsWith('[') && cleaned.endsWith(']')) cleaned = cleaned.slice(1, -1);
    if (cleaned.startsWith("'") && cleaned.endsWith("'")) cleaned = cleaned.slice(1, -1);
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) cleaned = cleaned.slice(1, -1);
    cleaned = cleaned.replace(/\(\?[ib]\)/g, '');
    return cleaned.trim();
  };

  const getCorrectAnswerText = (questionanswer_set) => {
    if(!questionanswer_set) return "";
    return questionanswer_set.map(ans => {
      let raw = (ans.answer || '').trim();
      if (raw.startsWith('[') && raw.endsWith(']')) {
        return raw.slice(1, -1).split(',').map(p => cleanAnswerText(p)).join(' / ');
      }
      return cleanAnswerText(raw);
    }).join(', ');
  };

  const renderEvaluationSection = ({ isSingleAnswerCorrect, isMultiAnswerCorrect }) => {
    const showMarks = disable && showMarksToStudent !== false;
    const showAnswerOnly = revealAnswers && (Eval || mode === "I");

    if (!showMarks && !showAnswerOnly) return null;

    return (
      <>
        <Col xs={6}></Col>
        <Col xs={6}>
          {showMarks && (
            <Form.Check>
              <Form.Label>Scored Marks</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={question?.questionresponse_set?.scored_marks}
                step="0.1"
                disabled
              />
            </Form.Check>
          )}
          {(Eval || mode === "I") && (
            <div style={{ marginRight: '-100px', marginTop: showMarks ? '-40px' : '0px', marginLeft: '-450px' }}>
              <AnswerExplanation explanation={question?.answer_explaination} correct_answer_text={getCorrectAnswerText(question?.questionanswer_set)} />
            </div>
          )}
        </Col>
      </>
    );
  };

  const { isSingleAnswerCorrect, isMultiAnswerCorrect } = validateUserAnswer(previousResponse?.response_text || "");

  return (
    <Row className="fill-blanks-component" style={{ margin: '0 10px' }}>
      <Col xs={12} style={{ marginBottom: 10 }}>
        <div className="question-header d-flex justify-content-between align-items-center">
          <b>
            <i>
              <span style={{ fontSize: 20, color: 'rgb(41, 121, 255)' }}>{`Question ${index + 1}`}</span>
            </i>
          </b>
          <div className="question-meta text-primary">
            <b>
              <i>
                {(() => {
                  const blankConfig = getBlankConfiguration();

                  if (blankConfig?.blanks?.length > 1) {
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <span>Question Type: Fill Blank(s)</span>
                        <div style={{ textAlign: 'right', marginLeft: '20px' }}>
                          {blankConfig.blanks.map((blank, blankIndex) => (
                            <div key={blankIndex} style={{ marginBottom: '2px' }}>
                              Blank {blankIndex + 1} - Marks: {blank.marks}, Negative: {blank.neg}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Legacy single blank display
                  return (
                    <span>
                      Question Type: Fill Blank(s)
                      <span style={{ marginLeft: '40px' }}>
                        Marks: {question.marks}{question.negative_marks ? `, Negative: ${question.negative_marks % 1 === 0 ? Math.floor(question.negative_marks) : question.negative_marks}` : ''}
                      </span>
                    </span>
                  );
                })()}
              </i>
            </b>
          </div>
        </div>
      </Col>
      {renderQuestionText()}
      {renderAnswerInput({ isSingleAnswerCorrect, isMultiAnswerCorrect })}
      {renderEvaluationSection({ isSingleAnswerCorrect, isMultiAnswerCorrect })}
    </Row>
  );
};

export default FillBlanks;
